import React, { createContext, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { createTheme, ThemeProvider } from "@material-ui/core";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import LoadRouteComponent from "./components/elements/loadRouter/LoadRouter";
import Layout from "./components/layout/Layout";
import { Helmet, HelmetProvider } from "react-helmet-async";
import publicRoutes from "./public-routes";
import routes from "./routes";
import getUserInfo from "./utils/getUserInfo";
import isAuthenticated from "./utils/isAuthenticated";
import AutoLogoutMessage from "./components/elements/autoLogout/AutoLogoutMessage";
import SuspenseSpinner from "./components/elements/spinner/SuspenseSpinner";

import "./assets/fonts/cp-icons/style.css";
import "./assets/fonts/social-icons/style.css";

import { GlobalStyle } from "./components/styles/globalStyle";
import { StyledRootContainer } from "./components/styles/styledMain";
import "rc-dropdown/assets/index.css";
import "rc-tooltip/assets/bootstrap.css";

import "./i18n";
import i18n from "i18next";
import { DEFAULT_LANGUAGE, HELMET_HTML_BY_LANG, SUPPORTED_LANGUAGES, THEME_COLOR } from "./utils/consts";
import { getCookie } from "./utils/coockies.utils";
import getLanguageFromUrl from "./utils/getLanguageFromUrl";
import { getInitialThemeName, getThemeByName, THEME_STORAGE_KEY, THEME_SWITCHING_CLASS } from "./components/styles/theme/theme";

export const AppContext = createContext({});

const createMuiThemeByMode = (themeMode) => createTheme({
  palette: {
    type: themeMode.name,
    background: {
      default: themeMode.body.background,
      paper: themeMode.menu.background,
    },
  },
  custom: themeMode,
  overrides: {
    MuiMenu: {
      paper: {
        // Added to prevent selects from being above the header
        maxHeight: "calc(100% - 108px)",
      },
    },
  },
});

const App = () => {

  const [authentication, setAuthentication] = useState(isAuthenticated);
  const [showExchanger, setShowExchanger] = useState(false);
  const [themeName, setThemeName] = useState(getInitialThemeName);

  const themeMode = useMemo(() => getThemeByName(themeName), [themeName]);
  const muiTheme = useMemo(() => createMuiThemeByMode(themeMode), [themeMode]);

  const toggleTheme = useCallback(() => {
    document.documentElement.classList.add(THEME_SWITCHING_CLASS);

    setThemeName(prevThemeName => {
      const nextThemeName = prevThemeName === THEME_COLOR.LIGHT ? THEME_COLOR.DARK : THEME_COLOR.LIGHT;

      localStorage.setItem(THEME_STORAGE_KEY, nextThemeName);

      return nextThemeName;
    });

    // Знімаємо клас лише після того як браузер відмалює нову тему, інакше transition встигне запуститись
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.documentElement.classList.remove(THEME_SWITCHING_CLASS);
      });
    });
  }, []);

  const lngFromUrl = getLanguageFromUrl();

  let baseNameLang = lngFromUrl === DEFAULT_LANGUAGE ? "/" : SUPPORTED_LANGUAGES[lngFromUrl].url;

  useEffect(() => {
    i18n.on("languageChanged", function (lng) {
        let currentUlr = new URL(document.location);

        let tmp = currentUlr.href.replace(currentUlr.origin, "").replace((lngFromUrl === DEFAULT_LANGUAGE ? "" : "/" + lngFromUrl), "");
        if (getCookie("i18next") !== lngFromUrl) {
          baseNameLang = DEFAULT_LANGUAGE !== getCookie["i18next"] ? "/" : lng;

          window.location.replace(currentUlr.origin + SUPPORTED_LANGUAGES[lng].url + tmp);
        }
      }
    );

    return () => {
      i18n.off("languageChanged");
    };
  }, []);

  const generateRoutes = (routes) => {
    return (
      routes.map((route, i) => (
        <LoadRouteComponent key={i} {...route} />
      ))
    );
  };

  const authRouteRender = () => {
    return authentication ? generateRoutes(routes) : generateRoutes(publicRoutes);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <StyledThemeProvider theme={themeMode}>
        <StyledRootContainer>
          <HelmetProvider>
            <Helmet>
              <html lang={HELMET_HTML_BY_LANG[lngFromUrl]} />
            </Helmet>
            <AutoLogoutMessage authentication={authentication} setAuthentication={setAuthentication} />
            <Router basename={baseNameLang}>
              <Suspense fallback={<SuspenseSpinner />}>
                <AppContext.Provider
                  value={{
                    user: getUserInfo(),
                    exchanger: { showExchanger, setShowExchanger },
                    theme: { themeName, toggleTheme }
                  }}
                >
                  <Layout
                    authentication={authentication}
                    setAuthentication={setAuthentication}
                    authRouteRender={authRouteRender}
                  />
                </AppContext.Provider>
              </Suspense>
            </Router>
          </HelmetProvider>
          <GlobalStyle />
        </StyledRootContainer>
      </StyledThemeProvider>
    </ThemeProvider>
  );
};

export default App;
